/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { useSelector } from "react-redux";

import {
  Label,
  Link,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import { SidePanel } from "Components/SidePanel/SidePanel";
import {
  getIsLoadingActivityEventDetails,
  getSelectedActivityEvent,
  getSelectedActivityEventErrorMessage,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import {
  formatDateAsString,
  getEventDateAsLocalDateString,
  getEventTimeText,
} from "Utility/FormatHelper";

import {
  buttonStyles,
  detailsHeaderTextStyles,
  detailsStackItemStyles,
  detailsStackTokens,
  detailsTextStyles,
  buttonContainerStyles,
} from "./ApplicationDetails.styles";
import { IActivityEventDetailsSidePanelProps } from "./IActivityEventDetailsSidePanelProps";

export const ActivityEventDetailsSidePanel = (
  props: IActivityEventDetailsSidePanelProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, previousEventId, nextEventId, onEventIdSelected } = props;

  const isLoading = useSelector(getIsLoadingActivityEventDetails);
  const selectedActivityEvent = useSelector(getSelectedActivityEvent);
  const errorMessage = useSelector(getSelectedActivityEventErrorMessage);
  const isEvent = selectedActivityEvent?.contributionType === "Event";

  return (
    <SidePanel
      isOpen={isPanelOpen}
      title={!!selectedActivityEvent ? `${selectedActivityEvent.contributionType} Details` : ""}
      onDismiss={(): void => setIsPanelOpen(false)}
      additionalFooterContent={
        <Stack horizontal styles={buttonContainerStyles}>
          <PrimaryButton
            styles={buttonStyles}
            onClick={() => {
              if (previousEventId !== null) {
                onEventIdSelected(previousEventId);
              }
            }}
            disabled={previousEventId === null} // Disable button if no previous event
          >
            Prev
          </PrimaryButton>
          <PrimaryButton
            styles={buttonStyles}
            onClick={() => {
              if (nextEventId !== null) {
                onEventIdSelected(nextEventId);
              }
            }}
            disabled={nextEventId === null} // Disable button if no next event
          >
            Next
          </PrimaryButton>
        </Stack>
      }
    >
      {!!isLoading ? (
        <Spinner size={SpinnerSize.large} />
      ) : !!errorMessage ? (
        <p>{errorMessage}</p>
      ) : (
        <Stack>
          <Stack>
            <Text styles={detailsHeaderTextStyles}>{selectedActivityEvent?.contributionType}</Text>
          </Stack>
          <Stack tokens={detailsStackTokens}>
            {isEvent && (
              <Stack horizontal tokens={detailsStackTokens}>
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Classification</Label>
                  <Text>
                    {!!selectedActivityEvent?.isPrivate
                      ? "Private: This event is not publicly searchable"
                      : "Public: This event is publicly searchable"}
                  </Text>
                </StackItem>
              </Stack>
            )}
            <Stack horizontal tokens={detailsStackTokens}>
              <StackItem styles={detailsStackItemStyles}>
                <Label>Title</Label>
                <Text>{selectedActivityEvent?.activityEventName}</Text>
              </StackItem>
              <StackItem styles={detailsStackItemStyles}>
                <Label>{`${selectedActivityEvent?.contributionType} Type`}</Label>
                <Text>{selectedActivityEvent?.activityEventType}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal tokens={detailsStackTokens}>
              <StackItem styles={detailsStackItemStyles}>
                <Label>Description</Label>
                <Text styles={detailsTextStyles}>{selectedActivityEvent?.description}</Text>
              </StackItem>
              {!!selectedActivityEvent?.privateDescription && (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Private Description</Label>
                  <Text>{selectedActivityEvent?.privateDescription}</Text>
                </StackItem>
              )}
            </Stack>
            <Stack horizontal tokens={detailsStackTokens}>
              <StackItem styles={detailsStackItemStyles}>
                <Label>Target Audiences</Label>
                <Text>{selectedActivityEvent?.targetAudiences.join(", ")}</Text>
              </StackItem>
              {isEvent && (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Event Format</Label>
                  <Text>{selectedActivityEvent?.eventFormat}</Text>
                </StackItem>
              )}
            </Stack>
            <Stack horizontal tokens={detailsStackTokens}>
              <StackItem styles={detailsStackItemStyles}>
                <Label>Primary Technology Area</Label>
                <Text>{selectedActivityEvent?.technologyArea}</Text>
              </StackItem>
              {!!selectedActivityEvent?.additionalTechnologyAreas &&
                selectedActivityEvent?.additionalTechnologyAreas.length > 0 && (
                  <StackItem styles={detailsStackItemStyles}>
                    <Label>Additional Technology Areas</Label>
                    <Text>{selectedActivityEvent?.additionalTechnologyAreas.join(", ")}</Text>
                  </StackItem>
                )}
            </Stack>
            <Stack horizontal tokens={detailsStackTokens}>
              <StackItem styles={detailsStackItemStyles}>
                <Label>{`${selectedActivityEvent?.contributionType} URL`}</Label>
                <Link href={selectedActivityEvent?.activityOrEventUrl} target="_blank">
                  {selectedActivityEvent?.activityOrEventUrl}
                </Link>
              </StackItem>
              {isEvent && (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Photos/Recording URL</Label>
                  <Link href={selectedActivityEvent?.photosRecordingUrl} target="_blank">
                    {selectedActivityEvent?.photosRecordingUrl}
                  </Link>
                </StackItem>
              )}
            </Stack>
            <Stack horizontal tokens={detailsStackTokens}>
              <StackItem styles={detailsStackItemStyles}>
                <Label>Quantity</Label>
                <Text>{selectedActivityEvent?.quantity}</Text>
              </StackItem>
            </Stack>
            <Stack horizontal tokens={detailsStackTokens}>
              {!isEvent && (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Live Stream Views</Label>
                  <Text>{selectedActivityEvent?.liveStreamViews}</Text>
                </StackItem>
              )}
              {isEvent && (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Live Online Views</Label>
                  <Text>{selectedActivityEvent?.liveOnlineViews}</Text>
                </StackItem>
              )}
              <StackItem styles={detailsStackItemStyles}>
                <Label>In-Person Attendees</Label>
                <Text>{selectedActivityEvent?.inPersonAttendees}</Text>
              </StackItem>
              <StackItem styles={detailsStackItemStyles}>
                <Label>On-Demand Views</Label>
                <Text>{selectedActivityEvent?.onDemandViews}</Text>
              </StackItem>
            </Stack>
            {!isEvent && (
              <Stack horizontal tokens={detailsStackTokens}>
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Subscriber Base</Label>
                  <Text>{selectedActivityEvent?.subscriberBase}</Text>
                </StackItem>
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Number Of Sessions</Label>
                  <Text>{selectedActivityEvent?.numberOfSessions}</Text>
                </StackItem>
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Number Of Views</Label>
                  <Text>{selectedActivityEvent?.numberOfViews}</Text>
                </StackItem>
              </Stack>
            )}
            <Stack horizontal tokens={detailsStackTokens}>
              {isEvent ? (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Time Zone</Label>
                  <Text>{selectedActivityEvent?.eventTimeZone}</Text>
                </StackItem>
              ) : (
                <StackItem styles={detailsStackItemStyles}>
                  <Label>Published Date</Label>
                  <Text>
                    {selectedActivityEvent?.activityPublishedDate &&
                      formatDateAsString(selectedActivityEvent.activityPublishedDate)}
                  </Text>
                </StackItem>
              )}
            </Stack>
            {isEvent && (
              <Stack horizontal tokens={detailsStackTokens}>
                {!!selectedActivityEvent?.eventStartDateTimeUtc && (
                  <StackItem styles={detailsStackItemStyles}>
                    <Label>Start Date</Label>
                    <Text>
                      {getEventDateAsLocalDateString(
                        selectedActivityEvent?.eventStartDateTimeUtc,
                        selectedActivityEvent.timeZoneOffset
                      )}
                    </Text>
                  </StackItem>
                )}
                {!!selectedActivityEvent?.eventEndDateTimeUtc && (
                  <StackItem styles={detailsStackItemStyles}>
                    <Label>End Date</Label>
                    <Text>
                      {getEventDateAsLocalDateString(
                        selectedActivityEvent?.eventEndDateTimeUtc,
                        selectedActivityEvent.timeZoneOffset
                      )}
                    </Text>
                  </StackItem>
                )}
              </Stack>
            )}
            {isEvent && (
              <Stack horizontal tokens={detailsStackTokens}>
                {!!selectedActivityEvent?.eventStartDateTimeUtc && (
                  <StackItem styles={detailsStackItemStyles}>
                    <Label>Start Time</Label>
                    <Text>
                      {getEventTimeText(
                        selectedActivityEvent?.eventStartDateTimeUtc,
                        selectedActivityEvent.timeZoneOffset
                      )}
                    </Text>
                  </StackItem>
                )}
                {!!selectedActivityEvent?.eventEndDateTimeUtc && (
                  <StackItem styles={detailsStackItemStyles}>
                    <Label>End Time</Label>
                    <Text>
                      {getEventTimeText(
                        selectedActivityEvent?.eventEndDateTimeUtc,
                        selectedActivityEvent.timeZoneOffset
                      )}
                    </Text>
                  </StackItem>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
    </SidePanel>
  );
};
